import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import type { ReactNode } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Plus } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { usePromoteTicket, useAddTrackerCategory, extractErrorMessage } from '@/queries/ticketMutations'
import { useTrackerCategories } from '@/queries/tracker'
import { useIsAdmin } from '@/lib/auth'

// Select value for the "New category" item; never a real category.
const NEW_CATEGORY = '__new_category__'

const schema = z.object({
  category: z.string().min(1, 'Choose a category'),
  impact: z.string().optional(),
})
type FormVals = z.infer<typeof schema>

export function PromoteDialog({
  ticketId,
  companyId,
  trigger,
  onPromoted,
}: {
  ticketId: string
  companyId: string
  trigger: ReactNode
  onPromoted?: () => void
}) {
  const [open, setOpen] = useState(false)
  const [newCategory, setNewCategory] = useState<string | null>(null)
  const isAdmin = useIsAdmin()
  const promote = usePromoteTicket(ticketId)
  const addCategory = useAddTrackerCategory(companyId)
  // Deferred until the dialog is actually open — companyId is undefined (and
  // the query disabled, see useTrackerCategories) while closed, so mounting
  // this dialog ahead of a click (e.g. inside TrackerControls) never fires a
  // request nobody asked for.
  const categoriesQuery = useTrackerCategories(open ? companyId : undefined)
  const categories = categoriesQuery.data ?? []
  const form = useForm<FormVals>({
    resolver: zodResolver(schema),
    defaultValues: { category: '', impact: '' },
  })

  const saveNewCategory = async () => {
    const name = newCategory?.trim()
    if (!name) return
    try {
      const saved = await addCategory.mutateAsync(name)
      form.setValue('category', saved.category, { shouldValidate: true })
      setNewCategory(null)
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not add the category'))
    }
  }

  const onSubmit = form.handleSubmit(async values => {
    if (newCategory !== null || addCategory.isPending) return
    try {
      await promote.mutateAsync({ category: values.category, impact: values.impact?.trim() || undefined })
      toast.success('Ticket added to the tracker')
      setOpen(false)
      form.reset()
      onPromoted?.()
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not promote ticket'))
    }
  })

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        setOpen(next)
        if (!next) {
          form.reset()
          setNewCategory(null)
        }
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Track this issue</DialogTitle>
          <DialogDescription>
            Tracking adds this to the client&apos;s tracker. Everyone at their company will be able to see it,
            including the full conversation.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4">
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  {/* '' is ignored: Radix's hidden native select reports '' when the value is set in the
                      same render its option arrives (a just-added category), which would clear it. */}
                  <Select
                    value={field.value}
                    onValueChange={value => {
                      if (value === NEW_CATEGORY) setNewCategory('')
                      else if (value) field.onChange(value)
                    }}
                    disabled={categoriesQuery.isLoading}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={categoriesQuery.isLoading ? 'Loading categories…' : 'Choose a category'} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categories.map(category => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                      {isAdmin && (
                        <>
                          <SelectSeparator />
                          <SelectItem value={NEW_CATEGORY}>
                            <Plus />
                            New category…
                          </SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  {newCategory !== null && (
                    <div className="flex items-center gap-2">
                      <Input
                        autoFocus
                        aria-label="New category"
                        placeholder="Category name"
                        maxLength={120}
                        value={newCategory}
                        onChange={e => setNewCategory(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            saveNewCategory()
                          }
                          if (e.key === 'Escape') {
                            e.stopPropagation()
                            setNewCategory(null)
                          }
                        }}
                      />
                      <Button type="button" size="sm" disabled={!newCategory.trim() || addCategory.isPending} onClick={saveNewCategory}>
                        Add
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setNewCategory(null)}>
                        Cancel
                      </Button>
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="impact"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Impact (optional)</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} placeholder="What's the impact on the client?" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              {/* Blocked while a new category is being named or saved, or the category chosen before it would be promoted. */}
              <Button type="submit" disabled={promote.isPending || categoriesQuery.isLoading || newCategory !== null || addCategory.isPending}>
                {promote.isPending ? 'Promoting…' : 'Track this issue'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
